import React, { useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import "../Css/ChapterMap.css";

// Public topojson of US states, fetched client-side — no bundled map data.
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Full state name (as given by the topojson) -> USPS abbreviation
const STATE_ABBR = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
  Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH",
  Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT",
  Virginia: "VA", Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
  "District of Columbia": "DC",
};

// Every chapter city: map position + the state it belongs to.
const CHAPTER_LOCATIONS = [
  { city: "Seattle, WA", state: "WA", coordinates: [-122.3321, 47.6062] },
  { city: "Tri-Valley, CA", state: "CA", coordinates: [-121.9358, 37.7021] },
  { city: "Fremont, CA", state: "CA", coordinates: [-121.9886, 37.5485] },
  { city: "San Jose, CA", state: "CA", coordinates: [-121.8863, 37.3382] },
  { city: "Milpitas, CA", state: "CA", coordinates: [-121.9, 37.4323] },
  { city: "Los Angeles, CA", state: "CA", coordinates: [-118.2437, 34.0522] },
  { city: "San Diego, CA", state: "CA", coordinates: [-117.1611, 32.7157] },
  { city: "Phoenix, AZ", state: "AZ", coordinates: [-112.074, 33.4484] },
  { city: "Houston, TX", state: "TX", coordinates: [-95.3698, 29.7604] },
  { city: "New Jersey", state: "NJ", coordinates: [-74.4057, 40.0583] },
];

// A state is highlighted whenever it has any chapter — independent of
// whether that chapter already has a photo gallery (dots below are
// limited to those so every pin has something to open on click).
const ACTIVE_STATES = new Set(CHAPTER_LOCATIONS.map((l) => l.state));

const ChapterMap = ({ chapters, onSelectChapter }) => {
  const [hovered, setHovered] = useState(null); // { city, x, y }

  // Show a dot for every chapter; only chapters with a photo gallery
  // open a popup on click (others still show a tooltip on hover).
  const locations = CHAPTER_LOCATIONS;

  const showTooltip = (city) => (e) => {
    setHovered({ city, x: e.clientX, y: e.clientY });
  };
  const moveTooltip = (e) => {
    setHovered((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h));
  };
  const hideTooltip = () => setHovered(null);

  return (
    <div className="chapter-map-wrap">
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 950 }}
        width={800}
        height={466}
        className="chapter-map"
        role="img"
        aria-label="Map of SportsForEquity chapter locations across the United States"
      >
        <Geographies geography={GEO_URL}>
          {({ geographies, outline }) => (
            <>
              {geographies.map((geo) => {
                const abbr = STATE_ABBR[geo.properties.name];
                const isActive = ACTIVE_STATES.has(abbr);

                return (
                  <React.Fragment key={geo.rsmKey}>
                    <Geography
                      geography={geo}
                      className={
                        isActive
                          ? "chapter-map-state chapter-map-state--active"
                          : "chapter-map-state"
                      }
                    />
                    {isActive && (
                      <Marker coordinates={geoCentroid(geo)}>
                        <text className="chapter-map-state-label" textAnchor="middle" dy="0.35em">
                          {abbr}
                        </text>
                      </Marker>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Bold outer coastline/contour of the whole country */}
              {outline && <path d={outline.svgPath} className="chapter-map-outline" />}
            </>
          )}
        </Geographies>

        {locations.map(({ city, coordinates }) => {
          const hasGallery = chapters.includes(city);
          return (
            <Marker
              key={city}
              coordinates={coordinates}
              onMouseEnter={showTooltip(city)}
              onMouseMove={moveTooltip}
              onMouseLeave={hideTooltip}
              onClick={hasGallery ? () => onSelectChapter(city) : undefined}
            >
              <circle
                r={4.5}
                className={
                  hasGallery
                    ? "chapter-map-pin"
                    : "chapter-map-pin chapter-map-pin--static"
                }
              />
              {hasGallery && <circle r={4.5} className="chapter-map-pin-pulse" />}
            </Marker>
          );
        })}
      </ComposableMap>

      {hovered && (
        <div
          className="chapter-map-tooltip"
          style={{ left: hovered.x + 14, top: hovered.y + 14 }}
        >
          {hovered.city}
        </div>
      )}
    </div>
  );
};

export default ChapterMap;